import React from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, gamingAPI, paymentsAPI } from '../services/api';
import { Gamepad2 } from 'lucide-react';

const formatHours = (hours) => {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return '0m';
  const totalMin = Math.round(h * 60);
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hrs <= 0) return `${mins}m`;
  return mins ? `${hrs}h ${mins}m` : `${hrs}h`;
};

const formatMkd = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0.00 MKD';
  return `${n.toFixed(2)} MKD`;
};

const LIVE = ['pending', 'starting', 'running', 'creating', 'stopping'];
const PLAYABLE = ['running'];
const BOOTING = ['pending', 'starting', 'creating'];

const sessionLabel = (status) => {
  const key = String(status || '').toLowerCase();
  if (PLAYABLE.includes(key)) return 'Playing';
  if (BOOTING.includes(key)) return 'Starting';
  if (key === 'stopping') return 'Closing';
  if (key === 'completed' || key === 'stopped') return 'Ended';
  if (key === 'orphaned' || key === 'failed' || key === 'error') return 'Could not close';
  return 'Ended';
};

const sessionTone = (status) => {
  const key = String(status || '').toLowerCase();
  if (PLAYABLE.includes(key)) return 'bg-sky-600 text-white';
  if (BOOTING.includes(key) || key === 'stopping') return 'bg-amber-700/80 text-amber-50';
  if (key === 'orphaned' || key === 'failed' || key === 'error') {
    return 'bg-white/15 text-amber-100';
  }
  return 'bg-white/10 text-white/80';
};

const Dashboard = () => {
  const { user } = useAuth();

  const { data: userStats, isLoading: statsLoading } = useQuery(
    'userStats',
    usersAPI.getUserStats,
    { refetchInterval: 10000 }
  );

  const { data: sessions, isLoading: sessionsLoading } = useQuery(
    'gamingSessions',
    gamingAPI.getSessions,
    { refetchInterval: 10000 }
  );

  const { data: cashback } = useQuery(
    'cashback',
    paymentsAPI.getCashback,
    { refetchInterval: 10000 }
  );

  const { data: lemonSub } = useQuery(
    'lemonSubscription',
    paymentsAPI.getLemonSubscription,
    { refetchInterval: 15000 }
  );

  const liveSession = sessions?.sessions?.find((session) =>
    LIVE.includes(session.status)
  );
  const playCredit = userStats?.play_credit ?? userStats?.wallet_balance ?? cashback?.play_credit ?? 0;
  const subscribed = Boolean(userStats?.subscription_active || lemonSub?.active || user?.is_admin);
  const suggestedGpu = userStats?.suggested_gpu || userStats?.preferred_gpu_type || 'RTX 4060';
  const planLine = userStats?.plan_line
    || (userStats?.family_quad || user?.family_quad
      ? 'Family access · 4 seats · GPUs from 12 GB up · stream Full HD'
      : 'Solo access · one player · GPUs from 8 GB Full HD up');
  const weekLine = userStats?.week_access_label
    || (userStats?.week_access === 'full'
      ? 'This week: full access (you launched)'
      : 'This week: idle access');
  const walletCap = Number(userStats?.max_wallet_mkd) || 20000;
  const playUrl = liveSession?.play_url;
  const canOpenPlay = Boolean(liveSession && PLAYABLE.includes(liveSession.status) && playUrl);

  const primary = (() => {
    if (!subscribed) {
      return {
        label: 'Subscribe on Profile',
        href: '/profile',
        hint: 'An Airpulse Game subscription is required to launch a GPU.',
      };
    }
    if (canOpenPlay) {
      return {
        label: 'Open Play',
        href: playUrl,
        external: true,
        hint: `${liveSession.session_name || 'Session'} · ${liveSession.gpu_type || suggestedGpu}`,
      };
    }
    if (liveSession) {
      return {
        label: 'Open Game',
        href: '/game',
        hint: sessionLabel(liveSession.status) === 'Closing'
          ? 'This machine is closing.'
          : 'The GPU is starting. Open Game to watch boot, then Play.',
      };
    }
    return {
      label: 'Choose a GPU',
      href: '/game',
      hint: `Suggested ${suggestedGpu}. Change on Profile.`,
    };
  })();

  const goPrimary = () => {
    if (primary.external) {
      window.location.href = primary.href;
      return;
    }
    window.location.href = primary.href;
  };

  if (statsLoading || sessionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  const recent = (sessions?.sessions || []).slice(0, 3);

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">
          Welcome back, {user?.username}
        </h1>
        <p className="text-white/75">{planLine}</p>
        {subscribed ? (
          <p className="text-white font-medium mt-2">{weekLine}</p>
        ) : (
          <p className="text-white/70 mt-2">Subscribe to launch a GPU.</p>
        )}
      </div>

      {subscribed && Array.isArray(userStats?.weekly_access) && userStats.weekly_access.length > 0 && (
        <div className="gaming-card rounded-lg p-4">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-sm font-semibold text-white/90">Last 4 weeks</h2>
            {typeof userStats.weekly_pending_rebate_eur === 'number' && userStats.weekly_pending_rebate_eur > 0 ? (
              <span className="text-xs text-white/70">
                Pending idle rebate at renewal: {userStats.weekly_pending_rebate_eur.toFixed(2)} EUR
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[...userStats.weekly_access].reverse().map((wk) => (
              <div
                key={wk.week_start}
                className={`rounded-lg px-3 py-2 text-xs border ${
                  wk.launched
                    ? 'bg-sky-500/20 border-sky-400/40 text-sky-100'
                    : 'bg-white/5 border-white/10 text-white/60'
                } ${wk.current ? 'ring-1 ring-white/40' : ''}`}
                title={wk.week_start}
              >
                <div className="font-medium">
                  {wk.launched ? 'Launched' : 'Idle'}
                </div>
                <div className="text-white/60">
                  {Number(wk.slice_eur || 0).toFixed(2)} EUR
                </div>
                {wk.current ? <div className="text-white/40 mt-1">this week</div> : null}
              </div>
            ))}
          </div>
          <p className="text-xs text-white/50 mt-3">
            Launched weeks bill the full slice. Idle weeks credit the difference back to
            the GPU wallet at monthly renewal.
          </p>
        </div>
      )}

      <div className="gaming-card rounded-lg p-6 space-y-4">
        <button
          type="button"
          onClick={goPrimary}
          className="play-button text-white px-6 py-3 rounded-lg font-semibold w-full"
        >
          <Gamepad2 className="inline h-5 w-5 mr-2" />
          {primary.label}
        </button>
        <p className="text-sm text-white/70">{primary.hint}</p>
        <p className="text-white">
          {formatMkd(playCredit)} GPU credit remaining
          <span className="text-white/55"> · cap {walletCap.toLocaleString()} MKD</span>
        </p>
        {subscribed ? (
          <p className="text-sm text-white/70">
            Suggested GPU: {suggestedGpu}
            {' · '}
            <a href="/profile" className="underline text-white/90">change on Profile</a>
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/70">
        <span>
          Hours played{' '}
          <span className="text-white font-medium">{formatHours(userStats?.total_hours_played)}</span>
        </span>
        <span>
          GPU rent spent{' '}
          <span className="text-white font-medium">{formatMkd(userStats?.total_spent)}</span>
        </span>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-white mb-3">Recent</h2>
        {recent.length > 0 ? (
          <div className="space-y-3">
            {recent.map((session) => (
              <div key={session.id} className="flex justify-between items-center gap-3">
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{session.session_name}</p>
                  <p className="text-white/60 text-sm">
                    {session.gpu_type} · {formatHours(session.total_hours || session.hours)} · {formatMkd(session.total_cost || session.cost)}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs shrink-0 ${sessionTone(session.status)}`}>
                  {sessionLabel(session.status)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/60">No sessions yet</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
